import { useState, useEffect } from "react";
import { Search, Filter, ArrowLeft, AlertTriangle, Shield, UserX, UserCheck, X } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";

interface Report {
  id: string;
  title: string;
  details: string;
  coachId: string;
  coachName: string;
  coachPhone: string;
  athleteId: string;
  athleteName: string;
  athletePhone: string;
  whoReported: "coach" | "athlete";
  disabled: boolean;
  userWarned: boolean;
  appealed: boolean;
  reportStatus: "open" | "closed" | "under_review" | "escalated";
  createdAt: string;
  updatedAt: string;
}

interface ActionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  actionType: "warn" | "terminate" | "reinstantiate" | "close";
  reportId: string;
  onSubmit: (comments: string) => void;
}

function ActionDialog({ isOpen, onClose, actionType, reportId, onSubmit }: ActionDialogProps) {
  const [comments, setComments] = useState("");

  const getActionInfo = () => {
    switch (actionType) {
      case "warn":
        return {
          title: "Warn User",
          description: "Issue a warning to the user",
          buttonColor: "bg-yellow-500 hover:bg-yellow-600",
          icon: <AlertTriangle className="h-4 w-4" />
        };
      case "terminate":
        return {
          title: "Terminate User",
          description: "Terminate the user's account",
          buttonColor: "bg-red-500 hover:bg-red-600",
          icon: <UserX className="h-4 w-4" />
        };
      case "reinstantiate":
        return {
          title: "Reinstantiate User",
          description: "Reinstantiate a terminated user account",
          buttonColor: "bg-green-500 hover:bg-green-600",
          icon: <UserCheck className="h-4 w-4" />
        };
      case "close":
        return {
          title: "Close Report",
          description: "Close this report",
          buttonColor: "bg-gray-500 hover:bg-gray-600",
          icon: <X className="h-4 w-4" />
        };
      default:
        return {
          title: "Action",
          description: "Take action on this report",
          buttonColor: "bg-primary hover:bg-primary/90",
          icon: <Shield className="h-4 w-4" />
        };
    }
  };

  const actionInfo = getActionInfo();

  const handleSubmit = () => {
    onSubmit(comments);
    setComments("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {actionInfo.icon}
            {actionInfo.title}
          </DialogTitle>
          <DialogDescription>
            {actionInfo.description}. Please provide your comments below.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="comments">Comments</Label>
            <div className="border rounded-lg">
              {/* Rich Text Editor Toolbar */}
              <div className="border-b p-2 flex flex-wrap items-center gap-1 bg-muted/50">
                <Button variant="ghost" size="sm" type="button">
                  <span className="font-bold">B</span>
                </Button>
                <Button variant="ghost" size="sm" type="button">
                  <span className="italic">I</span>
                </Button>
                <Button variant="ghost" size="sm" type="button">
                  <span className="underline">U</span>
                </Button>
                <div className="w-px h-6 bg-border mx-1" />
                <Button variant="ghost" size="sm" type="button">
                  •
                </Button>
                <Button variant="ghost" size="sm" type="button">
                  1.
                </Button>
                <div className="w-px h-6 bg-border mx-1" />
                <Select defaultValue="normal">
                  <SelectTrigger className="w-24 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="heading">Heading</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Text Area */}
              <Textarea
                id="comments"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Enter your comments regarding this action..."
                className="min-h-[200px] border-0 resize-none focus-visible:ring-0"
                required
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              className={actionInfo.buttonColor}
              disabled={!comments.trim()}
            >
              Submit
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function Reports() {
  const [reports, setReports] = useState<Report[]>([]);
  const [filteredReports, setFilteredReports] = useState<Report[]>([]);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentView, setCurrentView] = useState<"list" | "detail" | "review">("list");

  // Filter states
  const [searchTitle, setSearchTitle] = useState("");
  const [selectedReportStatus, setSelectedReportStatus] = useState<string>("all");
  const [selectedWhoReported, setSelectedWhoReported] = useState<string>("all");

  // Action dialog state
  const [actionDialog, setActionDialog] = useState<{
    isOpen: boolean;
    actionType: "warn" | "terminate" | "reinstantiate" | "close";
    reportId: string;
  }>({
    isOpen: false,
    actionType: "warn",
    reportId: ""
  });

  // Mock report data
  const mockReports: Report[] = [
    {
      id: "1",
      title: "Not good",
      details: "No improvement testing",
      coachId: "coach1",
      coachName: "Trey Beasley",
      coachPhone: "14695877105",
      athleteId: "athlete1", 
      athleteName: "Neon Jay",
      athletePhone: "13046022178",
      whoReported: "coach",
      disabled: false,
      userWarned: false,
      appealed: false,
      reportStatus: "closed",
      createdAt: "2024-07-28T10:00:00Z",
      updatedAt: "2024-07-30T15:30:00Z"
    },
    {
      id: "2",
      title: "Something",
      details: "Testing",
      coachId: "coach2",
      coachName: "Leonard Skynard", 
      coachPhone: "15551234567",
      athleteId: "athlete2",
      athleteName: "Athlete Test",
      athletePhone: "15557654321",
      whoReported: "coach",
      disabled: false,
      userWarned: false,
      appealed: false,
      reportStatus: "closed",
      createdAt: "2024-07-25T14:20:00Z",
      updatedAt: "2024-07-26T09:45:00Z"
    },
    {
      id: "3",
      title: "Typing issue",
      details: "Test",
      coachId: "coach3",
      coachName: "Leonard Skynard",
      coachPhone: "15551234567", 
      athleteId: "athlete3",
      athleteName: "John Smith",
      athletePhone: "15559876543",
      whoReported: "coach",
      disabled: false,
      userWarned: false,
      appealed: false,
      reportStatus: "closed",
      createdAt: "2024-07-20T16:15:00Z",
      updatedAt: "2024-07-22T11:20:00Z"
    },
    {
      id: "4",
      title: "something",
      details: "testing",
      coachId: "coach4",
      coachName: "Leonard Skynard",
      coachPhone: "15551234567",
      athleteId: "athlete4", 
      athleteName: "Athlete Test",
      athletePhone: "15557654321",
      whoReported: "athlete",
      disabled: false,
      userWarned: false,
      appealed: false,
      reportStatus: "closed",
      createdAt: "2024-07-18T13:30:00Z",
      updatedAt: "2024-07-19T08:15:00Z"
    }
  ];

  // Simulate API call to fetch reports
  useEffect(() => {
    const fetchReports = async () => {
      setIsLoading(true);
      try {
        // Simulate API call - replace with real API endpoint
        // const response = await fetch("host/api/v1/reports", {
        //   headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
        // });
        
        // Mock data for demonstration
        await new Promise(resolve => setTimeout(resolve, 800));
        
        setReports(mockReports);
        setFilteredReports(mockReports);
      } catch (error) {
        console.error("Failed to fetch reports:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (currentView === "list") {
      fetchReports();
    }
  }, [currentView]);

  // Apply filters
  useEffect(() => {
    let filtered = [...reports];

    // Title search
    if (searchTitle) {
      filtered = filtered.filter(report => 
        report.title.toLowerCase().includes(searchTitle.toLowerCase()) ||
        report.details.toLowerCase().includes(searchTitle.toLowerCase())
      );
    }

    // Report status filter
    if (selectedReportStatus !== "all") {
      filtered = filtered.filter(report => report.reportStatus === selectedReportStatus);
    }

    // Who reported filter
    if (selectedWhoReported !== "all") {
      filtered = filtered.filter(report => report.whoReported === selectedWhoReported);
    }

    setFilteredReports(filtered);
  }, [reports, searchTitle, selectedReportStatus, selectedWhoReported]);

  const clearFilters = () => {
    setSearchTitle("");
    setSelectedReportStatus("all");
    setSelectedWhoReported("all");
  };

  const handleReportClick = (report: Report) => {
    setSelectedReport(report);
    setCurrentView("detail");
  };

  const handleTakeAction = () => {
    setCurrentView("review");
  };

  const handleActionClick = (actionType: "warn" | "terminate" | "reinstantiate" | "close") => {
    setActionDialog({
      isOpen: true,
      actionType,
      reportId: selectedReport?.id || ""
    });
  };

  const handleActionSubmit = async (comments: string) => {
    try {
      // Simulate API call to take action
      // const response = await fetch(`host/api/v1/reports/${actionDialog.reportId}/action`, {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //     Authorization: `Bearer ${localStorage.getItem("authToken")}`
      //   },
      //   body: JSON.stringify({
      //     action: actionDialog.actionType,
      //     comments
      //   })
      // });

      console.log(`Taking action: ${actionDialog.actionType}`, {
        reportId: actionDialog.reportId,
        comments
      });

      // Update report status locally
      if (selectedReport) {
        const updatedReport = { ...selectedReport };
        if (actionDialog.actionType === "close") {
          updatedReport.reportStatus = "closed";
        } else if (actionDialog.actionType === "warn") {
          updatedReport.userWarned = true;
        }
        setSelectedReport(updatedReport);
      }

      setCurrentView("detail");
    } catch (error) {
      console.error("Failed to take action:", error);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "open":
        return <Badge variant="destructive">Open</Badge>;
      case "closed":
        return <Badge variant="secondary">Closed</Badge>;
      case "under_review":
        return <Badge variant="default">Under Review</Badge>;
      case "escalated":
        return <Badge variant="outline">Escalated</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (currentView === "review" && selectedReport) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => setCurrentView("detail")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1>Review</h1>
        </div>

        <Card>
          <CardContent className="p-6">
            <div className="space-y-6">
              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-4">
                <Button
                  variant="outline"
                  onClick={() => handleActionClick("warn")}
                  className="h-12 border-blue-200 text-blue-600 hover:bg-blue-50"
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Warn User
                </Button>
                <Button
                  onClick={() => handleActionClick("terminate")}
                  className="h-12 bg-red-500 hover:bg-red-600 text-white"
                >
                  <UserX className="h-4 w-4 mr-2" />
                  Terminate User
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleActionClick("reinstantiate")}
                  className="h-12"
                >
                  <UserCheck className="h-4 w-4 mr-2" />
                  Reinstantiate
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleActionClick("close")}
                  className="h-12 text-gray-600"
                >
                  <X className="h-4 w-4 mr-2" />
                  Close Report
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <ActionDialog
          isOpen={actionDialog.isOpen}
          onClose={() => setActionDialog(prev => ({ ...prev, isOpen: false }))}
          actionType={actionDialog.actionType}
          reportId={actionDialog.reportId}
          onSubmit={handleActionSubmit}
        />
      </div>
    );
  }

  if (currentView === "detail" && selectedReport) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => setCurrentView("list")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1>Show</h1>
        </div>

        <Card>
          <CardContent className="p-6 space-y-6">
            <div>
              <h3 className="mb-1">Title</h3>
              <p>{selectedReport.title}</p>
            </div>

            <div>
              <h3 className="mb-1">Details</h3>
              <p>{selectedReport.details}</p>
            </div>

            <div>
              <h3 className="mb-1">Coach</h3>
              <p>{selectedReport.coachName} ({selectedReport.coachPhone})</p>
            </div>

            <div>
              <h3 className="mb-1">Athlete</h3>
              <p>{selectedReport.athleteName} ({selectedReport.athletePhone})</p>
            </div>

            <div>
              <h3 className="mb-1">Reported By:</h3>
              <p>{selectedReport.whoReported}</p>
            </div>

            <div>
              <h3 className="mb-1">Report Status</h3>
              <p>{selectedReport.reportStatus}</p>
            </div>

            <div>
              <h3 className="mb-1">Action</h3>
              <Button 
                variant="outline" 
                className="mt-2"
                onClick={handleTakeAction}
              >
                Take Action
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="space-y-4">
          <div className="h-8 bg-muted animate-pulse rounded" />
          <div className="h-4 bg-muted animate-pulse rounded w-1/3" />
          <div className="space-y-2">
            {Array.from({ length: 10 }, (_, i) => (
              <div key={i} className="h-12 bg-muted animate-pulse rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h1>List</h1>
              <Badge variant="secondary" className="rounded-full">
                {filteredReports.length}
              </Badge>
            </div>
            <p className="text-muted-foreground">
              Manage and review user reports
            </p>
          </div>
        </div>
        <Button variant="outline">
          <Filter className="h-4 w-4 mr-2" />
          Filter
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Report List</CardTitle>
              <CardDescription>
                Filter and search through all reports
              </CardDescription>
            </div>
            <Button variant="outline" onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="text-sm mb-2 block">Search Title/Details</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search reports..."
                  value={searchTitle}
                  onChange={(e) => setSearchTitle(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <label className="text-sm mb-2 block">Report Status</label>
              <Select value={selectedReportStatus} onValueChange={setSelectedReportStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                  <SelectItem value="under_review">Under Review</SelectItem>
                  <SelectItem value="escalated">Escalated</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm mb-2 block">Who Reported</label>
              <Select value={selectedWhoReported} onValueChange={setSelectedWhoReported}>
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="coach">Coach</SelectItem>
                  <SelectItem value="athlete">Athlete</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Results Count */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-muted-foreground">
              Showing {filteredReports.length} of {reports.length} reports
            </p>
          </div>

          {/* Reports Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>Coach Id</TableHead>
                  <TableHead>Athlete Id</TableHead>
                  <TableHead>Who Reported</TableHead>
                  <TableHead>Disabled</TableHead>
                  <TableHead>User Warned</TableHead>
                  <TableHead>Appealed</TableHead>
                  <TableHead>Report Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReports.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8">
                      <div className="text-muted-foreground">
                        <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>No reports found matching your filters</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredReports.map((report) => (
                    <TableRow 
                      key={report.id} 
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleReportClick(report)}
                    >
                      <TableCell>{report.title}</TableCell>
                      <TableCell>
                        <div className="max-w-[150px] truncate" title={report.details}>
                          {report.details}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div>{report.coachName}</div>
                          <div className="text-sm text-muted-foreground">{report.coachPhone}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div>{report.athleteName}</div>
                          <div className="text-sm text-muted-foreground">{report.athletePhone}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {report.whoReported}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={report.disabled ? "destructive" : "secondary"}>
                          {report.disabled ? "Yes" : "No"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={report.userWarned ? "destructive" : "secondary"}>
                          {report.userWarned ? "Yes" : "No"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={report.appealed ? "destructive" : "secondary"}>
                          {report.appealed ? "Yes" : "No"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(report.reportStatus)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}